/*
 * Equation markup: an allowlist serialiser for MathML coming into the editor
 * (Word imports, pastes, MathLive output) and the <math> tag the editor stores.
 *
 * Stored form: <math display="block" data-latex="…" style="font-size:11pt">…</math>
 * The body is MathML Core only. data-latex is what MathLive edits; the browser
 * renders the MathML natively, so player views need no library.
 */

const ELEMENTS = new Set([
    'mrow', 'mi', 'mn', 'mo', 'ms', 'mtext', 'mspace', 'mfrac', 'msqrt', 'mroot', 'msub', 'msup', 'msubsup',
    'munder', 'mover', 'munderover', 'mtable', 'mtr', 'mtd', 'mpadded', 'mphantom', 'mstyle', 'merror',
]);

/** Elements whose text is content; whitespace elsewhere is insignificant. */
const TOKENS = new Set(['mi', 'mn', 'mo', 'ms', 'mtext']);

const ATTRIBUTES = new Set([
    'display', 'displaystyle', 'scriptlevel', 'linethickness', 'stretchy', 'symmetric', 'largeop', 'movablelimits',
    'fence', 'separator', 'form', 'lspace', 'rspace', 'minsize', 'maxsize', 'accent', 'accentunder', 'width',
    'height', 'depth', 'voffset', 'rowspan', 'columnspan', 'columnalign', 'rowalign', 'dir', 'mathcolor',
    'mathbackground', 'mathsize', 'mathvariant',
]);

const VALUE = /^[#\w.%,\s-]{0,64}$/;
const STYLE_PROPERTY = /^(?:font-size|color|background-color|text-align|border(?:-(?:top|right|bottom|left))?(?:-(?:style|width|color))?|padding(?:-(?:top|right|bottom|left))?)$/;

export const MATH_LIMITS = { maxChars: 100000, maxDepth: 64 };

const escapeText = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const escapeAttr = (s) => escapeText(s).replace(/"/g, '&quot;');
const nameOf = (el) => (el.localName || el.nodeName || '').toLowerCase();

/** Keeps only allowlisted declarations with plain values (no url(), no escapes). */
export function cleanStyle(style) {
    return String(style || '')
        .split(';')
        .map((decl) => {
            const i = decl.indexOf(':');
            if (i < 0) return null;
            const prop = decl.slice(0, i).trim().toLowerCase();
            const value = decl.slice(i + 1).trim();
            return value && STYLE_PROPERTY.test(prop) && VALUE.test(value) ? `${prop}:${value}` : null;
        })
        .filter(Boolean)
        .join(';');
}

function attributesOf(el) {
    let out = '';
    for (const { name, value } of Array.from(el.attributes)) {
        const n = name.toLowerCase();
        if (n === 'style') {
            const style = cleanStyle(value);
            if (style) out += ` style="${escapeAttr(style)}"`;
        } else if (ATTRIBUTES.has(n) && VALUE.test(value) && (n !== 'mathvariant' || value === 'normal')) {
            out += ` ${n}="${escapeAttr(value)}"`;
        }
    }
    return out;
}

class TooLarge extends Error {}

function serializeChildren(el, depth, state) {
    if (depth > MATH_LIMITS.maxDepth) throw new TooLarge();
    const keepText = TOKENS.has(nameOf(el));
    let out = '';
    for (const node of Array.from(el.childNodes)) {
        if (node.nodeType === 3) {
            if (keepText || node.data.trim()) out += escapeText(node.data);
        } else if (node.nodeType === 1) {
            out += serializeElement(node, depth + 1, state);
        }
        if (out.length > MATH_LIMITS.maxChars) throw new TooLarge();
    }
    return out;
}

function serializeElement(el, depth, state) {
    const name = nameOf(el);
    if (name === 'semantics') {
        // The first child is the presentation; a TeX annotation is the editable source.
        const children = Array.from(el.children);
        for (const ann of children.slice(1)) {
            if (state.tex === null && nameOf(ann) === 'annotation' && /^(application\/x-tex|tex|latex)$/i.test(ann.getAttribute('encoding') || '')) {
                state.tex = ann.textContent.trim();
            }
        }
        return children[0] ? serializeElement(children[0], depth, state) : '';
    }
    // A nested <math> is just a group.
    const tag = name === 'math' ? 'mrow' : name;
    if (!ELEMENTS.has(tag)) return '';
    return `<${tag}${name === 'math' ? '' : attributesOf(el)}>${serializeChildren(el, depth, state)}</${tag}>`;
}

/**
 * @param {Element} math a <math> element (any namespace)
 * @returns {{mathml: string, latex: string, display: 'inline'|'block', style: string} | null}
 *   null when it isn't usable math (empty, too large or too deep)
 */
export function sanitizeMath(math) {
    if (!math || nameOf(math) !== 'math') return null;
    const state = { tex: null };
    let mathml;
    try {
        mathml = serializeChildren(math, 0, state);
    } catch (err) {
        if (err instanceof TooLarge) return null;
        throw err;
    }
    if (!mathml.trim()) return null;
    const latex = math.getAttribute('data-latex') ?? state.tex ?? '';
    if (latex.length > MATH_LIMITS.maxChars) return null;
    return {
        mathml,
        latex,
        display: (math.getAttribute('display') || '').toLowerCase() === 'block' ? 'block' : 'inline',
        style: cleanStyle(math.getAttribute('style')),
    };
}

/** The stored <math> tag for sanitised equation data. */
export function mathToHtml({ mathml, latex, display, style }) {
    return `<math${display === 'block' ? ' display="block"' : ''}`
        + `${latex ? ` data-latex="${escapeAttr(latex)}"` : ''}`
        + `${style ? ` style="${escapeAttr(style)}"` : ''}>${mathml}</math>`;
}

/** Parses an HTML/MathML string and sanitises its first <math> element. */
export function parseMath(html) {
    const source = /^\s*<math[\s>]/i.test(html) ? html : `<math>${html}</math>`;
    const doc = new DOMParser().parseFromString(source, 'text/html');
    return sanitizeMath(doc.querySelector('math'));
}
